import React, { useState } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { MdFileDownload } from 'react-icons/md';
import toast from 'react-hot-toast';
import type { CalendarDay } from '../../redux/types/attendanceTypes';
import { formatTime } from './shared';
import CustomSelect, { monthOptions, yearOptions } from './CustomSelect';
import axiosInstance from '../../axios';

interface Props {
  onBack: () => void;
  employeeId: string;
  employeeName: string;
  designation: string;
  profilePicture?: string | null;
  calendar: CalendarDay[];
  year: number;
  month: number; // 1-12
  loading?: boolean;
  onMonthChange: (year: number, month: number) => void;
}

const MONTH_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface StatusStyle {
  cell: string;
  dot: string;
  label: string;
  text: string;
}

const STATUS: Record<string, StatusStyle> = {
  PRESENT:  { cell: 'bg-emerald-50 border-emerald-200',  dot: 'bg-emerald-400', label: 'Present',  text: 'text-emerald-600' },
  ABSENT:   { cell: 'bg-red-50 border-red-200',          dot: 'bg-red-400',     label: 'Absent',   text: 'text-red-500'     },
  HALF_DAY: { cell: 'bg-sky-50 border-sky-200',          dot: 'bg-sky-400',     label: 'Half Day', text: 'text-sky-600'     },
  ON_LEAVE: { cell: 'bg-amber-50 border-amber-200',      dot: 'bg-amber-400',   label: 'On Leave', text: 'text-amber-600'   },
  WEEKEND:  { cell: 'bg-gray-50 border-gray-100',        dot: '',               label: '',         text: 'text-gray-300'    },
  UPCOMING: { cell: 'bg-white border-gray-100',          dot: '',               label: '',         text: 'text-gray-800'    },
};

const AttendanceCalendarView: React.FC<Props> = ({
  onBack, employeeId, employeeName, designation, profilePicture,
  calendar, year, month, loading = false, onMonthChange,
}) => {
  const today = new Date();
  const [exporting, setExporting] = useState(false);

  const handleMonthChange = (m: number) => onMonthChange(year, m);
  const handleYearChange  = (y: number) => onMonthChange(y, month);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ employeeId, year: String(year), month: String(month) });
      const res = await axiosInstance.get(`/v1/admin/attendance/export?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const disp = res.headers['content-disposition'] as string | undefined;
      const match = disp?.match(/filename="(.+)"/);
      a.download = match?.[1] ?? `attendance_${year}_${String(month).padStart(2, '0')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Monday-start offset
  let firstDay = new Date(year, month - 1, 1).getDay();
  firstDay = firstDay === 0 ? 6 : firstDay - 1;

  const totalDays     = new Date(year, month, 0).getDate();
  const trailingBlanks = (7 - ((firstDay + totalDays) % 7)) % 7;

  const dayMap = new Map(calendar.map(d => [new Date(d.date).getUTCDate(), d]));

  // Summary counts
  const present  = calendar.filter(d => d.status === 'PRESENT').length;
  const absent   = calendar.filter(d => d.status === 'ABSENT').length;
  const onLeave  = calendar.filter(d => d.status === 'ON_LEAVE').length;
  const halfDay  = calendar.filter(d => d.status === 'HALF_DAY').length;

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack}
            className="w-9 h-9 flex items-center justify-center rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600">
            <IoIosArrowBack size={18} />
          </button>
          {profilePicture ? (
            <img src={profilePicture} alt={employeeName} className="w-11 h-11 rounded-full object-cover" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#3B3BB7]/10 flex items-center justify-center text-[#3B3BB7] font-bold text-base uppercase">
              {employeeName[0]}
            </div>
          )}
          <div>
            <p className="font-bold text-gray-900 text-lg leading-tight">{employeeName}</p>
            <p className="text-xs text-gray-400">{designation}</p>
          </div>
        </div>

        {/* Month navigation + export */}
        <div className="flex items-center gap-2">
          <CustomSelect value={month} options={monthOptions()} onChange={handleMonthChange} />
          <CustomSelect
            value={year}
            options={yearOptions(1900, new Date().getFullYear() + 1)}
            onChange={handleYearChange}
          />
          <button
            onClick={handleExport}
            disabled={exporting}
            className="h-9 flex items-center gap-1 text-xs font-semibold px-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors disabled:opacity-50"
          >
            <MdFileDownload className="text-sm" />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>

      {/* ── Summary pills ── */}
      {!loading && (
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            { label: 'Present',  count: present, ...STATUS.PRESENT  },
            { label: 'Absent',   count: absent,  ...STATUS.ABSENT   },
            { label: 'On Leave', count: onLeave, ...STATUS.ON_LEAVE },
            { label: 'Half Day', count: halfDay, ...STATUS.HALF_DAY },
          ].map(s => (
            <div key={s.label}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${s.cell} ${s.text}`}>
              <span className={`w-2 h-2 rounded-full ${s.dot}`} />
              {s.label} <span className="font-bold">{s.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Calendar ── */}
      <div className={`bg-gray-50 rounded-2xl p-3 sm:p-4 transition-opacity ${loading ? 'opacity-40 pointer-events-none' : ''}`}>
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-[11px] font-semibold text-gray-400 py-2 tracking-wide uppercase">
              {d}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {/* Leading blanks */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`b${i}`} className="rounded-xl min-h-[52px] sm:min-h-[80px]" />
          ))}

          {/* Day cells */}
          {Array.from({ length: totalDays }, (_, i) => {
            const date    = i + 1;
            const day     = dayMap.get(date);
            const status  = day?.status ?? 'UPCOMING';
            const style   = STATUS[status] ?? STATUS.UPCOMING;
            const isToday = year === today.getFullYear() && month === today.getMonth() + 1 && date === today.getDate();
            const isWeekend  = status === 'WEEKEND';
            const isUpcoming = status === 'UPCOMING';

            return (
              <div
                key={date}
                title={day && !isWeekend && !isUpcoming ? [
                  style.label,
                  day.checkInTime  ? `In: ${formatTime(day.checkInTime)}`   : '',
                  day.checkOutTime ? `Out: ${formatTime(day.checkOutTime)}` : '',
                  day.workingHours ? `${day.workingHours.toFixed(1)}h`       : '',
                ].filter(Boolean).join(' · ') : ''}
                className={`
                  relative rounded-xl border min-h-[52px] sm:min-h-[80px] p-1.5 sm:p-2 flex flex-col
                  ${isToday ? 'ring-2 ring-[#3B3BB7] ring-offset-1' : ''}
                  ${style.cell}
                `}
              >
                {/* Date number */}
                <span className={`text-[11px] sm:text-sm font-semibold leading-none ${
                  isToday ? 'text-[#3B3BB7]' : isWeekend ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {date}
                </span>

                {/* Status — desktop label, mobile dot */}
                {!isWeekend && !isUpcoming && day && (
                  <>
                    {/* Desktop */}
                    <span className={`hidden sm:block mt-auto text-[10px] font-semibold ${style.text}`}>
                      {day.workingHours !== null
                        ? `${day.workingHours.toFixed(1)}h`
                        : style.label}
                    </span>
                    {/* Mobile dot */}
                    <span className={`sm:hidden mt-auto mx-auto w-2 h-2 rounded-full ${style.dot}`} />
                  </>
                )}

                {/* Today marker */}
                {isToday && (
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#3B3BB7]" />
                )}
              </div>
            );
          })}

          {/* Trailing blanks */}
          {Array.from({ length: trailingBlanks }).map((_, i) => (
            <div key={`e${i}`} className="rounded-xl min-h-[52px] sm:min-h-[80px]" />
          ))}
        </div>
      </div>

      {loading && (
        <p className="text-center text-sm text-gray-400 mt-4">Loading…</p>
      )}
    </div>
  );
};

export default AttendanceCalendarView;
